'use client'

import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { ReportPayload } from '@tebuto/api/public/models/ReportPayload'
import { Button } from '@tebuto/buttons/Button'
import FormCheckbox from '@tebuto/forms/Checkbox'
import ErrorMessage from '@tebuto/forms/ErrorMessage'
import FormField from '@tebuto/forms/Field'
import FormTextArea from '@tebuto/forms/TextArea'
import { useCurrentUser, useLoginStatus, usePublicApiMutation } from '@tebuto/functions/hooks'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { UNICODE_EMAIL_REGEX } from '@tebuto/functions/validate'
import { CurrentUserGuard } from '@tebuto/guards/CurrentUser'
import { LoginStatusGuard } from '@tebuto/guards/LoginStatus'
import { cn } from '@tebuto/lib/utils'
import TebutoModal from '@tebuto/modals/TebutoModal'
import { CaptchaContext, CaptchaProvider, CaptchaWidget } from '@tebuto/providers/CaptchaProvider'
import ExtraSmallText from '@tebuto/typography/ExtraSmallText'
import MediumText from '@tebuto/typography/MediumText'
import Link from 'next/link'
import { createContext, Dispatch, SetStateAction, useContext, useState } from 'react'
import { toast } from 'react-toastify'
import { boolean, object, string } from 'yup'

interface ReportProps {
    title: string
    contentPath: string
    className?: string
    iconOnly?: boolean
}

interface ReportContext {
    title: string
    contentPath: string
    show: boolean
    setShow: Dispatch<SetStateAction<boolean>>
    sent: boolean
    setSent: Dispatch<SetStateAction<boolean>>
}

interface ReportContent {
    email: string
    message: string
    agreeToDataPrivacy: boolean
}

const contactFormValidationSchema = object().shape({
    // default email validation does not support unicode characters
    email: string().required('Pflichtfeld').matches(UNICODE_EMAIL_REGEX, {
        message: 'Ungültige E-Mail-Adresse',
        name: 'email',
        excludeEmptyString: true
    }),
    message: string().required('Pflichtfeld'),
    agreeToDataPrivacy: boolean().isTrue('Sie müssen der Verarbeitung Ihrer Daten zustimmen.')
})

const ReportContext = createContext<ReportContext>({} as ReportContext)

export default function Report({ title, contentPath, className, iconOnly }: ReportProps) {
    const [show, setShow] = useState(false)
    const [sent, setSent] = useState(false)

    return (
        <LoginStatusGuard>
            <ReportContext.Provider value={{ title, contentPath, show, setShow, sent, setSent }}>
                <button
                    type="button"
                    onClick={() => setShow(true)}
                    title={title}
                    className={cn(
                        'group inline-flex items-center gap-1.5 rounded-md text-left text-foreground-muted transition-colors hover:text-primary-500',
                        className
                    )}
                >
                    <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0 transition-colors" />
                    {!iconOnly && <span className="text-xs font-medium leading-none">{title}</span>}
                </button>
                <ReportModal />
            </ReportContext.Provider>
        </LoginStatusGuard>
    )
}

function ReportModal() {
    const isLoggedIn = useLoginStatus()
    return (
        <CaptchaProvider>
            {isLoggedIn ? (
                <CurrentUserGuard>
                    <LoggedInReportModal />
                </CurrentUserGuard>
            ) : (
                <GenericReportModal />
            )}
        </CaptchaProvider>
    )
}

function LoggedInReportModal() {
    const user = useCurrentUser()
    return <GenericReportModal email={user.email} />
}

function GenericReportModal({ email = '' }: { email?: string }) {
    const { t } = useComponentTranslation()
    const { title, contentPath, show, setShow, sent, setSent } = useContext(ReportContext)
    const { captcha } = useContext(CaptchaContext)
    const isLoggedIn = useLoginStatus()
    const sendReport = useSendReport(setSent)

    async function send(values: ReportContent) {
        if (captcha) {
            sendReport({ ...values, captcha, subject: contentPath })
        } else {
            toast.error(t('report.noCaptchaFound'))
        }
    }

    function reset() {
        setShow(false)
        setTimeout(() => setSent(false), 1000)
    }

    return (
        <TebutoModal<ReportContent>
            isOpen={show}
            title={title}
            closeHandler={reset}
            onSubmit={send}
            className="max-w-xl"
            confirmButtonText={t('report.send')}
            initialValues={{ email, message: '', agreeToDataPrivacy: isLoggedIn }}
            validationSchema={contactFormValidationSchema}
            customButtonBar={
                sent && (
                    <div>
                        <Button onClick={reset}>{t('close')}</Button>
                    </div>
                )
            }
        >
            <ReportForm />
        </TebutoModal>
    )
}

function ReportForm() {
    const isLoggedIn = useLoginStatus()
    const { t } = useComponentTranslation()
    const { sent } = useContext(ReportContext)

    if (sent) {
        return (
            <MediumText className="text-green-700 bg-green-100 px-6 py-4 border-green-700 border rounded-lg">
                {t('report.success')}
            </MediumText>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="grid gap-2 mb-6">
                <MediumText>{t('report.text1')}</MediumText>
                <MediumText>{t('report.text2')}</MediumText>
            </div>
            <FormField
                label={t('report.yourEmailAddress')}
                name="email"
                type="email"
                mandatory
                autoComplete="email"
                readOnly={isLoggedIn}
            />
            <div>
                <FormTextArea label={t('report.message')} name="message" mandatory />
                <ExtraSmallText className="text-[0.625rem]">{t('report.messageInfo')}</ExtraSmallText>
            </div>
            {!isLoggedIn && (
                <div className={'flex items-center'}>
                    <FormCheckbox
                        name={'agreeToDataPrivacy'}
                        mandatory={true}
                        showErrors={false}
                        className={'flex items-center'}
                    />
                    <div className="ml-3 text-sm leading-6">
                        <label htmlFor="agreeToDataPrivacy" className="text-gray-500">
                            {t('report.privacyConsent')}{' '}
                            <Link href="/datenschutzerklaerung" className="text-primary underline">
                                {t('privacyPolicy')}
                            </Link>
                        </label>
                        <div className="relative">
                            <ErrorMessage name={'agreeToDataPrivacy'} />
                        </div>
                    </div>
                </div>
            )}
            <CaptchaWidget />
        </div>
    )
}

function useSendReport(setSent: Dispatch<SetStateAction<boolean>>) {
    const { trigger } = usePublicApiMutation(
        'sendReport',
        (api, values: ReportPayload) => api.report.sendReport(values),
        { onSuccess: () => setSent(true) }
    )
    return trigger
}
